import axios from 'axios';
import { pipeexRuntimeConfig } from '../config/pipeexRuntimeConfig';
import { normalizeMachineFingerprint } from '../lib/machineFingerprint';
import type { AuthGet, AuthState, AuthUser } from './authStoreTypes';

const API_BASE_URL = pipeexRuntimeConfig.apiBaseUrl;

type RuntimeSessionPayload = {
  username: string;
  machineFingerprint: string;
};

function readStringClaim(payload: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const value = payload[key];
    if (typeof value === 'string' && value) return value;
  }

  return undefined;
}

export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const payloadPart = token.split('.')[1];
    if (!payloadPart) return null;

    const base64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function decodeJwt(token: string): AuthUser | null {
  try {
    const payload = decodeJwtPayload(token);
    if (!payload) return null;

    return {
      id: readStringClaim(payload, 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier', 'nameid') || '',
      username: readStringClaim(payload, 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name', 'unique_name', 'username') || 'user',
      role: readStringClaim(payload, 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role', 'role'),
    };
  } catch {
    return null;
  }
}

function isPcfRuntimeToken(token: string | null): boolean {
  if (!token) return false;

  const payload = decodeJwtPayload(token);
  return !!payload && (payload.PcfLogin === 'true' || payload.PcfLogin === true);
}

export function parseStoredUser(raw: string | null): AuthUser | null {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<AuthUser>;
    if (!parsed || typeof parsed !== 'object') return null;
    if (typeof parsed.id !== 'string' || typeof parsed.username !== 'string') return null;

    return {
      id: parsed.id,
      username: parsed.username,
      role: typeof parsed.role === 'string' ? parsed.role : undefined,
    };
  } catch {
    return null;
  }
}

export function persistSession(accessToken: string, refreshToken: string, user: AuthUser): void {
  localStorage.setItem('accessToken', accessToken);
  localStorage.setItem('refreshToken', refreshToken);
  localStorage.setItem('user', JSON.stringify(user));
}

function buildRuntimeSessionPayload(state: AuthState): RuntimeSessionPayload | null {
  if (!state.user || !isPcfRuntimeToken(state.accessToken)) return null;

  const fingerprint = normalizeMachineFingerprint(state.machineFingerprint);
  if (state.machineFingerprint && !fingerprint) return null;
  return { username: state.user.username, machineFingerprint: fingerprint || '' };
}

export async function releaseRuntimeSession(
  get: AuthGet,
  preferKeepalive = false
): Promise<void> {
  let payload = buildRuntimeSessionPayload(get());
  if (!payload) return;

  if (!payload.machineFingerprint && !preferKeepalive) {
    await get().initializeFingerprint();
    payload = buildRuntimeSessionPayload(get());
    if (!payload) return;
  }

  const url = `${API_BASE_URL}/auth/logout-pcf`;
  if (preferKeepalive && typeof window !== 'undefined' && typeof window.fetch === 'function') {
    try {
      void window.fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      });
      return;
    } catch {
      // Fall back to axios below when keepalive fetch is unavailable.
    }
  }

  try {
    await axios.post(url, payload, { timeout: 5000 });
  } catch (error) {
    console.warn('[AuthStore] Runtime session release failed', error);
  }
}
